import {createAction, createSlice, PayloadAction} from '@reduxjs/toolkit'

import {
  offersSearchErrored,
  offersSearchStarted,
  offersSearchSucceeded,
  roomSearchErrored,
  roomSearchStarted,
  roomSearchSucceeded
} from '@vio/core/src/sapi/services/searchApi/action'

import type {Hotel} from '@findhotel/sapi'

export interface AccommodationGalleryState {
  isOverlayOpen: boolean
  overlaySelectedImage: string | undefined
}
export interface AccommodationState {
  hotelId: Hotel['objectID']
  searchId: string
  overlayExperimentIsInitialized: boolean
  isTopOfferVisible: boolean
  isRoomsViewExpanded: boolean
  lightbox: AccommodationGalleryState
  isRoomsFiltersLoading: boolean
  isStaticMapImageLoaded: boolean
  isRoomsFirstImageLoaded: boolean
  soldoutOfferIds: Record<string, boolean>
  shouldDisplayAlternativeOffers: boolean
  /**
   * Id of the offer whose offer-check failed with a recoverable site error
   * (transport/server/unexpected, not a confirmed sold-out). Drives a one-off
   * "couldn't confirm availability" toast and is cleared once shown.
   */
  offerCheckRetryableErrorId: string | null
  /**
   * Added as part of feature-offer-check-selected-offer-ap
   */
  isProactiveOfferCheckResolved: boolean
  /**
   * Hotel whose rooms long-poll failed. The rooms query itself never enters an
   * error state (failures are swallowed upstream), so consumers waiting on
   * polling completion need this to bail out instead of waiting forever.
   */
  roomsSearchErrorHotelId: string | null
  /**
   * Same escape for the top-offers search, which gates the rooms search: on an
   * offers failure the rooms request never even starts.
   */
  offersSearchErrorHotelId: string | null
  /** Clicked-offer params of the first rooms request for a hotel card clicker, so filtering the top offers doesn't refetch the rooms */
  roomsTopOfferLock: RoomsTopOfferLock | null
  /** SRP filters the AP was opened with, so room filters synced to the URL don't leak into the way back to the SRP */
  srpEntryFilters: SrpEntryFilters | null
}

export interface SrpEntryFilters {
  freeCancellation?: BooleanUrlParam
}

export interface RoomsTopOfferLock {
  hotelId: Hotel['objectID']
  /** Serialized rooms params without the top offer ones; a change releases the lock */
  searchKey: string
  params: {bundleId?: string; clickedOfferId?: string}
}

export const initialState: AccommodationState = {
  hotelId: null,
  searchId: null,
  overlayExperimentIsInitialized: false,
  isRoomsViewExpanded: false,
  isTopOfferVisible: undefined,
  lightbox: {
    isOverlayOpen: false,
    overlaySelectedImage: undefined
  },
  isRoomsFiltersLoading: false,
  isStaticMapImageLoaded: false,
  isRoomsFirstImageLoaded: false,
  soldoutOfferIds: {},
  shouldDisplayAlternativeOffers: false,
  offerCheckRetryableErrorId: null,
  isProactiveOfferCheckResolved: false,
  roomsSearchErrorHotelId: null,
  offersSearchErrorHotelId: null,
  roomsTopOfferLock: null,
  srpEntryFilters: null
}

const {reducer, actions} = createSlice({
  name: 'accommodation',
  initialState,
  reducers: {
    setAccommodationHotelId: (
      state,
      {payload}: PayloadAction<Hotel['objectID']>
    ) => {
      state.hotelId = payload
    },
    setRoomsTopOfferLock: (
      state,
      {payload}: PayloadAction<RoomsTopOfferLock>
    ) => {
      state.roomsTopOfferLock = payload
    },
    clearRoomsTopOfferLock: state => {
      state.roomsTopOfferLock = null
    },
    setSrpEntryFilters: (state, {payload}: PayloadAction<SrpEntryFilters>) => {
      state.srpEntryFilters = payload
    },
    clearSrpEntryFilters: state => {
      state.srpEntryFilters = null
    },
    resetAccommodationPageState: state => {
      state.isRoomsViewExpanded = false
      state.shouldDisplayAlternativeOffers = false
    },
    setTopOfferVisibility: (state, {payload}) => {
      state.isTopOfferVisible = payload.value
    },

    setLightboxOverlayVisibility: (
      state,
      {payload}: PayloadAction<boolean>
    ) => {
      state.lightbox.isOverlayOpen = payload
    },
    setLightboxSelectedImage: (
      state,
      {payload}: PayloadAction<string | undefined>
    ) => {
      state.lightbox.overlaySelectedImage = payload
    },
    expandRoomsView: state => {
      state.isRoomsViewExpanded = true
    },
    collapseRoomsView: state => {
      state.isRoomsViewExpanded = false
    },
    setRoomsFiltersLoading: (state, {payload}: PayloadAction<boolean>) => {
      state.isRoomsFiltersLoading = payload
    },
    setStaticMapImageLoaded: (state, {payload}: PayloadAction<boolean>) => {
      state.isStaticMapImageLoaded = payload
    },
    setRoomsFirstImageLoaded: (state, {payload}: PayloadAction<boolean>) => {
      state.isRoomsFirstImageLoaded = payload
    },
    setShouldDisplayAlternativeOffers: (
      state,
      {payload}: PayloadAction<boolean>
    ) => {
      state.shouldDisplayAlternativeOffers = payload
    },
    setSoldoutOfferById: (state, {payload}: PayloadAction<string>) => {
      state.soldoutOfferIds = {
        ...state.soldoutOfferIds,
        [payload]: true
      }
    },
    setProactiveOfferCheckResolved: (
      state,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isProactiveOfferCheckResolved = payload
    },
    setOfferCheckRetryableError: (state, {payload}: PayloadAction<string>) => {
      state.offerCheckRetryableErrorId = payload
    },
    clearOfferCheckRetryableError: state => {
      state.offerCheckRetryableErrorId = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(roomSearchErrored, (state, action) => {
        // Payload generic decays to unknown across the package boundary
        const params = (
          action.payload as {params?: {hotelId?: string}} | undefined
        )?.params
        state.roomsSearchErrorHotelId = params?.hotelId ?? null
      })
      // A started search makes the recorded failure stale: consumers must wait
      // for the retry's outcome instead of acting on the previous one
      .addCase(roomSearchStarted, state => {
        state.roomsSearchErrorHotelId = null
      })
      .addCase(roomSearchSucceeded, state => {
        state.roomsSearchErrorHotelId = null
      })
      .addCase(offersSearchErrored, (state, action) => {
        const parameters = (
          action.payload as {parameters?: {hotelId?: string}} | undefined
        )?.parameters
        state.offersSearchErrorHotelId = parameters?.hotelId ?? null
      })
      .addCase(offersSearchStarted, state => {
        state.offersSearchErrorHotelId = null
      })
      .addCase(offersSearchSucceeded, state => {
        state.offersSearchErrorHotelId = null
      })
  }
})

/**
 * Fires an action to tracks updates to AP
 */
export const markAccommodationPageLoad = createAction('accommodation/markLoad')

export const {
  setAccommodationHotelId,
  setRoomsTopOfferLock,
  clearRoomsTopOfferLock,
  setSrpEntryFilters,
  clearSrpEntryFilters,
  resetAccommodationPageState,
  setTopOfferVisibility,
  setLightboxOverlayVisibility,
  setLightboxSelectedImage,
  expandRoomsView,
  collapseRoomsView,
  setRoomsFiltersLoading,
  setStaticMapImageLoaded,
  setRoomsFirstImageLoaded,
  setShouldDisplayAlternativeOffers,
  setSoldoutOfferById,
  setProactiveOfferCheckResolved,
  setOfferCheckRetryableError,
  clearOfferCheckRetryableError
} = actions

export default reducer
